import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../scripts/AuthContext";
import { useOrgFilter } from "../scripts/OrgFilterContext";
import axios from "axios";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  LayoutDashboard,
  ListTodo,
  UsersRound,
  LogOut,
  Settings,
  ChevronDown,
  Mail,
  MessagesSquare,
  Hash,
  Globe2,
  Compass,
  UserRound,
} from "lucide-react";
import OrgMemberAvatar from "@/components/avatars/OrgMemberAvatar";
import SocialUserAvatar from "@/components/avatars/SocialUserAvatar";
import WorkspaceSwitcher from "@/components/WorkspaceSwitcher";
import { useSocialProfile } from "../scripts/SocialProfileContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { getFullName } from "@/lib/userUtils";
import { toastError } from "@/lib/toast";
import { cn } from "@/lib/utils";
import InvitationsList from "./InvitationsList";
import InviteUserDialog from "./InviteUserDialog";
import ChannelBrowser from "./ChannelBrowser";
import { getDmUnreadCount } from "@/lib/dmApi";
import { getPartnerUnreadCount } from "@/lib/partnerApi";
import { getSocialDmUnreadCount } from "@/lib/socialDmApi";
import { useDmSocket } from "../scripts/DmSocketContext";
import { useOptionalSocialDmSocket } from "../scripts/SocialDmSocketContext";
import { useOptionalPartnerSocket } from "../scripts/PartnerSocketContext";
import { useOptionalNotificationsSocket } from "../scripts/NotificationsSocketContext";
import { PRESENCE_OPTIONS, getOwnPresenceOption } from "@/lib/presence";
import { useDmAlerts } from "../scripts/DmAlertContext";

import { API_BASE_URL } from "@/lib/apiBase";
import { fetchUserMe, seedUserMe } from "@/lib/userMeCache";
import { clearAutoAwayActive } from "@/lib/autoAwayPresence";

const MY_WORK_ATTENTION_TYPES =
  "post.critical,work.mention,work.assignee,team.handoff,channel.here";

const AppSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, accessToken } = useAuth();
  const {
    selectedOrgId,
    workspaceUpdating,
    workspaceRefreshKey,
    workspaceSnapshot,
  } = useOrgFilter();
  const { complete: socialComplete, profile: socialProfile } =
    useSocialProfile();
  const { subscribe } = useDmSocket();
  const socialDmSocket = useOptionalSocialDmSocket();
  const socialSubscribe = socialDmSocket?.subscribe;
  const partnerSocket = useOptionalPartnerSocket();
  const partnerSubscribe = partnerSocket?.subscribe;
  const notificationsSocket = useOptionalNotificationsSocket();
  const notificationsSubscribe = notificationsSocket?.subscribe;
  const { setDesktopEnabled, desktopEnabled } = useDmAlerts();
  const { state: sidebarState, isMobile, setOpenMobile } = useSidebar();
  const sidebarCollapsed = sidebarState === "collapsed";

  useEffect(() => {
    if (isMobile) setOpenMobile(false);
  }, [location.pathname, isMobile, setOpenMobile]);
  const [user, setUser] = useState(null);
  const [memberAvatarUrl, setMemberAvatarUrl] = useState(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [showInvitationsDialog, setShowInvitationsDialog] = useState(false);
  const [showInvitePeopleDialog, setShowInvitePeopleDialog] = useState(false);
  const [showChannelBrowser, setShowChannelBrowser] = useState(false);
  const [pendingInvitationsCount, setPendingInvitationsCount] = useState(0);
  const [dmUnreadCount, setDmUnreadCount] = useState(0);
  const [socialDmUnreadCount, setSocialDmUnreadCount] = useState(0);
  const [myWorkBadgeCount, setMyWorkBadgeCount] = useState(0);
  const [communityPresenceEnabled, setCommunityPresenceEnabled] =
    useState(false);

  axios.defaults.withCredentials = true;

  const hasOrgSelected =
    selectedOrgId !== "social" && selectedOrgId !== null && selectedOrgId !== "";
  const isSocialWorkspace = !hasOrgSelected;

  // Fetch user data (shared cache — axios interceptor refreshes expired tokens)
  useEffect(() => {
    if (!accessToken) return;

    const fetchUserData = async () => {
      try {
        const data = await fetchUserMe(accessToken);
        setUser(data);
      } catch (error) {
        console.error("Failed to fetch user:", error);
      }
    };

    fetchUserData();
  }, [accessToken]);

  // Keep sidebar presence in sync with auto-away / other writers
  useEffect(() => {
    const onPresence = (e) => {
      const status = e?.detail;
      if (typeof status !== "string" || !status) return;
      setUser((prev) =>
        prev ? { ...prev, presence_status: status } : prev
      );
    };
    window.addEventListener("presence-changed", onPresence);
    return () => window.removeEventListener("presence-changed", onPresence);
  }, []);

  // Org-scoped avatar for the sidebar footer (active workplace only)
  useEffect(() => {
    if (!accessToken || !hasOrgSelected || !user?.user_id) {
      setMemberAvatarUrl(null);
      return undefined;
    }
    if (workspaceUpdating) return undefined;
    let cancelled = false;
    const applyFromMembers = (members) => {
      const mine = (members || []).find(
        (m) => m.user?.user_id === user.user_id || m.user_id === user.user_id
      );
      if (!cancelled) {
        setMemberAvatarUrl(
          mine?.member_avatar_url ||
            mine?.user?.member_avatar_url ||
            null
        );
      }
    };
    const load = async () => {
      try {
        if (
          workspaceSnapshot?.orgId === selectedOrgId &&
          Array.isArray(workspaceSnapshot.members)
        ) {
          applyFromMembers(workspaceSnapshot.members);
          return;
        }
        const response = await axios.get(
          `${API_BASE_URL}/org/${selectedOrgId}/members`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );
        applyFromMembers(response.data);
      } catch (err) {
        console.error("Failed to load membership avatar:", err);
        if (!cancelled) setMemberAvatarUrl(null);
      }
    };
    load();
    const onAvatarChanged = (event) => {
      if (String(event.detail?.orgId) !== String(selectedOrgId)) return;
      if (Object.prototype.hasOwnProperty.call(event.detail || {}, "url")) {
        setMemberAvatarUrl(event.detail.url || null);
      } else {
        load();
      }
    };
    window.addEventListener("member-avatar-changed", onAvatarChanged);
    return () => {
      cancelled = true;
      window.removeEventListener("member-avatar-changed", onAvatarChanged);
    };
  }, [
    accessToken,
    hasOrgSelected,
    selectedOrgId,
    user?.user_id,
    location.pathname,
    workspaceUpdating,
    workspaceRefreshKey,
    workspaceSnapshot,
  ]);

  // Unread DM badge for selected workplace org
  useEffect(() => {
    if (!accessToken || !hasOrgSelected) {
      setDmUnreadCount(0);
      return undefined;
    }
    if (workspaceUpdating) return undefined;
    let cancelled = false;
    const load = async (preferSnapshot = true) => {
      try {
        if (
          preferSnapshot &&
          workspaceSnapshot?.orgId === selectedOrgId &&
          workspaceSnapshot.unread
        ) {
          const dm = Number(workspaceSnapshot.unread.dm) || 0;
          const partner = Number(workspaceSnapshot.unread.partner) || 0;
          if (!cancelled) setDmUnreadCount(dm + partner);
          return;
        }
        const [dm, partner] = await Promise.all([
          getDmUnreadCount(accessToken, selectedOrgId),
          Promise.resolve(getPartnerUnreadCount(accessToken, selectedOrgId)).catch(
            () => 0
          ),
        ]);
        if (!cancelled) setDmUnreadCount((Number(dm) || 0) + (Number(partner) || 0));
      } catch (err) {
        console.error("Failed to load DM unread count:", err);
      }
    };
    load(true);
    const interval = setInterval(() => load(false), 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [
    accessToken,
    selectedOrgId,
    hasOrgSelected,
    location.pathname,
    workspaceUpdating,
    workspaceRefreshKey,
    workspaceSnapshot,
  ]);

  // My work badge: Attention unread only (hidden when 0)
  useEffect(() => {
    if (!accessToken || !hasOrgSelected) {
      setMyWorkBadgeCount(0);
      return undefined;
    }
    if (workspaceUpdating) return undefined;
    let cancelled = false;
    const load = async (preferSnapshot = true) => {
      try {
        if (
          preferSnapshot &&
          workspaceSnapshot?.orgId === selectedOrgId &&
          workspaceSnapshot.unread
        ) {
          if (!cancelled) {
            setMyWorkBadgeCount(
              Number(workspaceSnapshot.unread.notificationsAttention) || 0
            );
          }
          return;
        }
        const notifRes = await axios.get(`${API_BASE_URL}/notifications`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: {
            org_id: selectedOrgId,
            unread: "true",
            types: MY_WORK_ATTENTION_TYPES,
            limit: 1,
          },
        });
        if (cancelled) return;
        setMyWorkBadgeCount(
          Number(notifRes.data?.total ?? notifRes.data?.items?.length) || 0
        );
      } catch (err) {
        console.error("Failed to load My work badge:", err);
        if (!cancelled) setMyWorkBadgeCount(0);
      }
    };
    load(true);
    const onFocus = () => load(false);
    window.addEventListener("focus", onFocus);
    const interval = setInterval(() => load(false), 30000);
    const unsub = notificationsSubscribe
      ? notificationsSubscribe((event) => {
          if (
            event === "notification.created" ||
            event === "notification.unread_count"
          ) {
            load(false);
          }
        })
      : undefined;
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
      clearInterval(interval);
      unsub?.();
    };
  }, [
    accessToken,
    selectedOrgId,
    hasOrgSelected,
    location.pathname,
    workspaceUpdating,
    workspaceRefreshKey,
    notificationsSubscribe,
    workspaceSnapshot,
  ]);

  // Unread social DM badge
  useEffect(() => {
    if (!accessToken || !isSocialWorkspace) {
      setSocialDmUnreadCount(0);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const count = await getSocialDmUnreadCount(accessToken);
        if (!cancelled) setSocialDmUnreadCount(count);
      } catch (err) {
        console.error("Failed to load social DM unread count:", err);
      }
    };
    load();
    const interval = setInterval(load, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [accessToken, isSocialWorkspace, location.pathname, workspaceRefreshKey]);

  useEffect(() => {
    if (!socialSubscribe || !isSocialWorkspace) return undefined;
    return socialSubscribe((event) => {
      if (event === "social-dm.message" || event === "social-dm.read") {
        getSocialDmUnreadCount(accessToken)
          .then(setSocialDmUnreadCount)
          .catch(() => {});
      }
    });
  }, [socialSubscribe, isSocialWorkspace, accessToken]);

  // Community Space nav — only when Social presence is enabled
  useEffect(() => {
    if (!accessToken || !hasOrgSelected) {
      setCommunityPresenceEnabled(false);
      return undefined;
    }
    if (workspaceUpdating) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        if (
          workspaceSnapshot?.orgId === selectedOrgId &&
          workspaceSnapshot.socialPresence
        ) {
          if (!cancelled) {
            setCommunityPresenceEnabled(
              Boolean(workspaceSnapshot.socialPresence.enabled)
            );
          }
          return;
        }
        const res = await axios.get(
          `${API_BASE_URL}/org/${selectedOrgId}/social/presence`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        if (!cancelled) {
          setCommunityPresenceEnabled(Boolean(res.data?.enabled));
        }
      } catch {
        if (!cancelled) setCommunityPresenceEnabled(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [
    accessToken,
    selectedOrgId,
    hasOrgSelected,
    workspaceUpdating,
    workspaceRefreshKey,
    workspaceSnapshot,
  ]);

  useEffect(() => {
    if (!hasOrgSelected || !accessToken) return undefined;
    const refreshUnread = () => {
      Promise.all([
        getDmUnreadCount(accessToken, selectedOrgId),
        Promise.resolve(getPartnerUnreadCount(accessToken, selectedOrgId)).catch(
          () => 0
        ),
      ])
        .then(([dm, partner]) =>
          setDmUnreadCount((Number(dm) || 0) + (Number(partner) || 0))
        )
        .catch(() => {});
    };
    const unsubDm = subscribe((event, payload) => {
      if (String(payload?.org_id) !== String(selectedOrgId)) return;
      if (
        event === "dm.message" ||
        event === "dm.read" ||
        event === "dm.message.deleted" ||
        event === "dm.message.reaction"
      ) {
        refreshUnread();
      }
    });
    const unsubPartner = partnerSubscribe
      ? partnerSubscribe((event) => {
          if (
            event === "partner.message" ||
            event === "partner.read" ||
            event === "partner.message.deleted"
          ) {
            refreshUnread();
          }
        })
      : undefined;
    return () => {
      unsubDm?.();
      unsubPartner?.();
    };
  }, [
    subscribe,
    partnerSubscribe,
    hasOrgSelected,
    accessToken,
    selectedOrgId,
  ]);

  const setPresence = async (presence_status) => {
    if (!accessToken) return;
    try {
      clearAutoAwayActive();
      const res = await axios.patch(
        `${API_BASE_URL}/user/me`,
        { presence_status },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setUser(res.data);
      seedUserMe(accessToken, res.data);
      window.dispatchEvent(
        new CustomEvent("presence-changed", {
          detail: presence_status,
        })
      );
    } catch (err) {
      console.error("Failed to update presence:", err);
    }
  };

  const presence = getOwnPresenceOption(user?.presence_status);
  const sidebarUser = user
    ? { ...user, member_avatar_url: memberAvatarUrl }
    : null;

  // Pending invitations count — hydrate from enter snapshot when present
  useEffect(() => {
    if (!accessToken) return undefined;

    const fetchPendingCount = async () => {
      try {
        if (
          workspaceSnapshot?.orgId === selectedOrgId &&
          typeof workspaceSnapshot.invitationsPendingCount === "number"
        ) {
          setPendingInvitationsCount(workspaceSnapshot.invitationsPendingCount);
          return;
        }
        const response = await axios.get(`${API_BASE_URL}/invitations`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: {
            type: "received",
            status: "pending",
          },
        });
        setPendingInvitationsCount(response.data?.length || 0);
      } catch (error) {
        console.error("Failed to fetch pending invitations count:", error);
      }
    };

    fetchPendingCount();
    return undefined;
  }, [accessToken, selectedOrgId, workspaceSnapshot, workspaceRefreshKey]);

  useEffect(() => {
    if (!notificationsSubscribe) return undefined;
    return notificationsSubscribe((event, payload) => {
      if (event === "invites.pending_count" && payload?.count != null) {
        setPendingInvitationsCount(Number(payload.count) || 0);
      }
    });
  }, [notificationsSubscribe]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isActive = (path) => {
    if (path === "#") return false;
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  const myTeamsPath = hasOrgSelected ? `/orgs/${selectedOrgId}` : null;
  const myWorkPath = hasOrgSelected
    ? `/o/${encodeURIComponent(selectedOrgId)}/my-work`
    : null;
  const messagesPath = hasOrgSelected
    ? `/o/${encodeURIComponent(selectedOrgId)}/messages`
    : "/messages";

  const communityPath = hasOrgSelected
    ? `/orgs/${encodeURIComponent(selectedOrgId)}/social`
    : null;
  const communityPathPrefix = hasOrgSelected
    ? `/orgs/${encodeURIComponent(selectedOrgId)}/social`
    : null;
  const legacyCommunityPath = hasOrgSelected
    ? `/orgs/${encodeURIComponent(selectedOrgId)}/community`
    : null;

  const handleMyTeamsClick = () => {
    if (!hasOrgSelected) {
      toastError("Select a workspace first");
      return;
    }
    if (myTeamsPath) {
      navigate(myTeamsPath);
    }
  };

  const handleMessagesClick = () => {
    // Social plane Messages is always available; workplace Messages needs an org.
    if (isSocialWorkspace) {
      navigate("/messages");
      return;
    }
    if (!hasOrgSelected) {
      toastError("Select a workspace first");
      return;
    }
    navigate(messagesPath);
  };

  const handleChannelsClick = () => {
    if (!hasOrgSelected) {
      toastError("Select a workspace first");
      return;
    }
    setShowChannelBrowser(true);
  };

  const menuItems = isSocialWorkspace
    ? [
        {
          title: "Home",
          url: "/dashboard",
          icon: LayoutDashboard,
          tourId: "nav-home",
        },
        {
          title: "Explore",
          url: "/explore",
          icon: Compass,
        },
        ...(socialComplete && socialProfile?.username
          ? [
              {
                title: "Profile",
                url: `/u/${encodeURIComponent(socialProfile.username)}`,
                icon: UserRound,
              },
            ]
          : []),
        {
          title: "Messages",
          url: "#",
          icon: MessagesSquare,
          action: "messages",
          tourId: "nav-messages",
        },
        {
          title: "Invitations",
          url: "#",
          icon: Mail,
          action: "invitations",
        },
      ]
    : [
        {
          title: "Dashboard",
          url: "/dashboard",
          icon: LayoutDashboard,
          tourId: "nav-home",
        },
        {
          title: "My work",
          url: myWorkPath || "#",
          icon: ListTodo,
          action: "my-work",
          tourId: "nav-my-work",
        },
        {
          title: "Teams",
          url: "#",
          icon: UsersRound,
          action: "my-teams",
          tourId: "org-teams",
        },
        {
          title: "Messages",
          url: "#",
          icon: MessagesSquare,
          action: "messages",
          tourId: "nav-messages",
        },
        {
          title: "Channel Browser",
          url: "#",
          icon: Hash,
          action: "channels",
        },
        ...(communityPresenceEnabled && communityPath
          ? [
              {
                title: "Circa Social",
                url: communityPath,
                icon: Globe2,
                action: "community",
              },
            ]
          : []),
        {
          title: "Invitations",
          url: "#",
          icon: Mail,
          action: "invitations",
        },
      ];

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="border-b border-sidebar-border">
        <WorkspaceSwitcher />
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isInvitations = item.action === "invitations";
                const isMyTeams = item.action === "my-teams";
                const isMyWork = item.action === "my-work";
                const isMessages = item.action === "messages";
                const isChannels = item.action === "channels";
                const isCommunity = item.action === "community";
                const active = isChannels
                  ? showChannelBrowser
                  : isMyWork
                    ? Boolean(
                        myWorkPath &&
                          (location.pathname === myWorkPath ||
                            location.pathname.startsWith(`${myWorkPath}/`))
                      )
                    : isMyTeams
                      ? Boolean(myTeamsPath && location.pathname === myTeamsPath)
                      : isMessages
                        ? location.pathname === "/messages" ||
                          location.pathname.startsWith("/messages/") ||
                          (location.pathname.startsWith("/o/") &&
                            location.pathname.includes("/messages"))
                        : isCommunity
                          ? Boolean(
                              communityPathPrefix &&
                                (location.pathname === communityPathPrefix ||
                                  location.pathname.startsWith(
                                    `${communityPathPrefix}/`
                                  ) ||
                                  (legacyCommunityPath &&
                                    (location.pathname === legacyCommunityPath ||
                                      location.pathname.startsWith(
                                        `${legacyCommunityPath}/`
                                      ))))
                            )
                          : isActive(item.url);
                const myTeamsDisabled = isMyTeams && !hasOrgSelected;
                const myWorkDisabled = isMyWork && !hasOrgSelected;
                const messagesDisabled =
                  isMessages && !isSocialWorkspace && !hasOrgSelected;
                const channelsDisabled = isChannels && !hasOrgSelected;
                const itemDisabled =
                  myTeamsDisabled ||
                  myWorkDisabled ||
                  messagesDisabled ||
                  channelsDisabled;
                const tooltipLabel = itemDisabled
                  ? "Select a workspace first"
                  : item.title;

                const badgeCount = isMessages
                  ? isSocialWorkspace
                    ? socialDmUnreadCount
                    : dmUnreadCount
                  : isInvitations
                    ? pendingInvitationsCount
                    : isMyWork
                      ? myWorkBadgeCount
                      : 0;
                const badgeLabel = isMessages
                  ? badgeCount > 99
                    ? "99+"
                    : String(badgeCount)
                  : isInvitations
                    ? badgeCount > 9
                      ? "9+"
                      : String(badgeCount)
                    : isMyWork
                      ? badgeCount > 99
                        ? "99+"
                        : String(badgeCount)
                      : "";

                return (
                  <SidebarMenuItem
                    key={item.title}
                    data-tour={item.tourId || undefined}
                  >
                    <SidebarMenuButton
                      asChild={
                        item.url !== "#" &&
                        !isInvitations &&
                        !isMyTeams &&
                        !isMyWork &&
                        !isMessages &&
                        !isChannels
                      }
                      isActive={active}
                      tooltip={tooltipLabel}
                      aria-disabled={itemDisabled || undefined}
                      title={tooltipLabel}
                      onClick={
                        isInvitations
                          ? () => setShowInvitationsDialog(true)
                          : isMyWork
                            ? () => {
                                if (!myWorkPath) {
                                  toastError("Select a workspace first");
                                  return;
                                }
                                navigate(myWorkPath);
                              }
                            : isMyTeams
                            ? handleMyTeamsClick
                            : isMessages
                              ? handleMessagesClick
                              : isChannels
                                ? handleChannelsClick
                                : undefined
                      }
                      className={cn(
                        itemDisabled && "opacity-50 cursor-not-allowed",
                        // Room for expanded-mode badge so the label doesn't collide
                        badgeCount > 0 && "pr-8"
                      )}
                    >
                      {item.url !== "#" &&
                      !isInvitations &&
                      !isMyTeams &&
                      !isMyWork &&
                      !isMessages &&
                      !isChannels ? (
                        <Link to={item.url}>
                          <Icon />
                          <span>{item.title}</span>
                        </Link>
                      ) : (
                        <>
                          <Icon />
                          <span>{item.title}</span>
                        </>
                      )}
                    </SidebarMenuButton>
                    {badgeCount > 0 && (
                      <>
                        {/* Expanded: sibling of button so overflow-hidden cannot clip it */}
                        <SidebarMenuBadge
                          className="bg-destructive text-destructive-foreground h-5 min-w-5 px-1.5 text-[10px] font-medium"
                          aria-label={`${badgeLabel} unread`}
                        >
                          {badgeLabel}
                        </SidebarMenuBadge>
                        {/* Collapsed icon mode: SidebarMenuBadge is hidden; pin a visible chip */}
                        <span
                          aria-hidden
                          className="pointer-events-none absolute -right-0.5 -top-0.5 z-10 hidden h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-medium text-destructive-foreground group-data-[collapsible=icon]:flex"
                        >
                          {badgeLabel}
                        </span>
                      </>
                    )}
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border">
        <SidebarMenu>
          <SidebarMenuItem>
            {isSocialWorkspace ? (
              sidebarCollapsed ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <SidebarMenuButton
                      tooltip={
                        socialProfile?.display_name ||
                        (socialProfile?.username
                          ? `@${socialProfile.username}`
                          : "Profile")
                      }
                      data-tour="nav-profile"
                    >
                      <SocialUserAvatar
                        user={socialProfile}
                        avatarUrl={socialProfile?.avatar_url}
                        displayName={socialProfile?.display_name}
                        className="h-6 w-6"
                        fallbackClassName="bg-primary/10 text-primary text-[10px]"
                      />
                    </SidebarMenuButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent side="right" align="end" className="w-52">
                    {socialProfile?.username ? (
                      <DropdownMenuItem
                        onClick={() =>
                          navigate(
                            `/u/${encodeURIComponent(socialProfile.username)}`
                          )
                        }
                        className="cursor-pointer"
                      >
                        View Social profile
                      </DropdownMenuItem>
                    ) : null}
                    <DropdownMenuItem
                      onClick={() => navigate("/profile?tab=bots")}
                      className="cursor-pointer"
                    >
                      My bots
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/profile?tab=account")}
                      className="cursor-pointer"
                    >
                      Circa account
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-sidebar-accent"
                      data-testid="nav-profile"
                    >
                      <SocialUserAvatar
                        user={socialProfile}
                        avatarUrl={socialProfile?.avatar_url}
                        displayName={socialProfile?.display_name}
                        className="h-8 w-8"
                        fallbackClassName="bg-primary/10 text-primary text-xs"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">
                          {socialProfile?.display_name || "Social profile"}
                        </p>
                        {socialProfile?.username ? (
                          <p className="text-xs text-muted-foreground truncate">
                            @{socialProfile.username}
                          </p>
                        ) : null}
                      </div>
                      <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-52">
                    {socialProfile?.username ? (
                      <DropdownMenuItem
                        onClick={() =>
                          navigate(
                            `/u/${encodeURIComponent(socialProfile.username)}`
                          )
                        }
                        className="cursor-pointer"
                      >
                        View Social profile
                      </DropdownMenuItem>
                    ) : null}
                    <DropdownMenuItem
                      onClick={() => navigate("/profile?tab=bots")}
                      className="cursor-pointer"
                    >
                      My bots
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/profile?tab=account")}
                      className="cursor-pointer"
                    >
                      Circa account
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )
            ) : sidebarCollapsed ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    tooltip={getFullName(user) || "Profile"}
                    data-tour="nav-profile"
                    className="relative"
                  >
                    <OrgMemberAvatar
                      user={sidebarUser}
                      className="h-6 w-6"
                      fallbackClassName="bg-primary text-primary-foreground text-[10px]"
                    />
                    <span
                      className={`absolute bottom-0.5 right-0.5 h-2 w-2 rounded-full border border-sidebar ${presence.dot}`}
                    />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="right" align="end" className="w-48">
                  <DropdownMenuItem
                    onClick={() => navigate("/profile")}
                    className="cursor-pointer"
                  >
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate("/profile?tab=bots")}
                    className="cursor-pointer"
                  >
                    My bots
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {PRESENCE_OPTIONS.map((opt) => (
                    <DropdownMenuItem
                      key={opt.value}
                      onClick={() => setPresence(opt.value)}
                      className="cursor-pointer gap-2"
                    >
                      <span className={`h-2 w-2 rounded-full ${opt.dot}`} />
                      {opt.label}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onSelect={async (e) => {
                      e.preventDefault();
                      await setDesktopEnabled(!desktopEnabled);
                    }}
                  >
                    Desktop notifications: {desktopEnabled ? "On" : "Off"}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2 px-2 py-1.5">
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-full relative"
                  data-tour="nav-profile"
                  onClick={() => navigate("/profile")}
                  title={getFullName(user) || "Profile"}
                >
                  <OrgMemberAvatar
                    user={sidebarUser}
                    className="h-8 w-8"
                    fallbackClassName="bg-primary text-primary-foreground text-xs"
                  />
                  <span
                    className={`absolute bottom-0.5 right-0.5 h-2.5 w-2.5 rounded-full border-2 border-sidebar ${presence.dot}`}
                    title={presence.label}
                  />
                </Button>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {getFullName(user) || "User"}
                  </p>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground truncate max-w-full"
                      >
                        <span
                          className={`h-1.5 w-1.5 rounded-full shrink-0 ${presence.dot}`}
                        />
                        <span className="truncate">{presence.label}</span>
                        <ChevronDown className="h-3 w-3 shrink-0 opacity-60" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-48">
                      {PRESENCE_OPTIONS.map((opt) => (
                        <DropdownMenuItem
                          key={opt.value}
                          onClick={() => setPresence(opt.value)}
                          className="cursor-pointer gap-2"
                        >
                          <span className={`h-2 w-2 rounded-full ${opt.dot}`} />
                          {opt.label}
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onSelect={async (e) => {
                          e.preventDefault();
                          await setDesktopEnabled(!desktopEnabled);
                        }}
                      >
                        Desktop notifications: {desktopEnabled ? "On" : "Off"}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            )}
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip="Settings">
              <Link
                to={
                  isSocialWorkspace
                    ? "/profile?tab=account"
                    : "/profile?tab=settings"
                }
              >
                <Settings />
                <span>Settings</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <Dialog open={logoutDialogOpen} onOpenChange={setLogoutDialogOpen}>
              <DialogTrigger asChild>
                <SidebarMenuButton tooltip="Logout">
                  <LogOut />
                  <span>Logout</span>
                </SidebarMenuButton>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Confirm Logout</DialogTitle>
                  <DialogDescription>
                    Are you sure you want to logout?
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setLogoutDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button variant="destructive" onClick={handleLogout}>
                    Logout
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      {hasOrgSelected && (
        <ChannelBrowser
          open={showChannelBrowser}
          onOpenChange={setShowChannelBrowser}
          orgId={selectedOrgId}
        />
      )}

      {/* Invitations Dialog */}
      <Dialog open={showInvitationsDialog} onOpenChange={setShowInvitationsDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-start justify-between gap-3 pr-6">
              <div className="min-w-0">
                <DialogTitle>Invitations</DialogTitle>
                <DialogDescription>
                  View and manage your invitations
                  {!hasOrgSelected
                    ? " Switch to a workplace to invite people."
                    : ""}
                </DialogDescription>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0"
                disabled={!hasOrgSelected}
                title={
                  hasOrgSelected
                    ? undefined
                    : "Switch to a workplace to invite people."
                }
                onClick={() => {
                  setShowInvitationsDialog(false);
                  setShowInvitePeopleDialog(true);
                }}
              >
                Invite people
              </Button>
            </div>
          </DialogHeader>
          <InvitationsList
            initialType="all"
            initialStatus="pending"
            onInvitationUpdate={() => {
              // Refresh pending count when invitations are updated
              const fetchPendingCount = async () => {
                try {
                  const response = await axios.get(`${API_BASE_URL}/invitations`, {
                    headers: {
                      Authorization: `Bearer ${accessToken}`,
                    },
                    params: {
                      type: "received",
                      status: "pending",
                    },
                  });
                  setPendingInvitationsCount(response.data?.length || 0);
                } catch (error) {
                  console.error("Failed to fetch pending invitations count:", error);
                }
              };
              fetchPendingCount();
            }}
          />
        </DialogContent>
      </Dialog>
      <InviteUserDialog
        isOpen={showInvitePeopleDialog}
        onClose={() => setShowInvitePeopleDialog(false)}
        orgId={hasOrgSelected ? selectedOrgId : null}
      />
    </Sidebar>
  );
};

export default AppSidebar;

